import type { PaymentDetail } from '@/modules/payments/services/payments.service';
import type { PortalOverview } from '@/modules/portal/types/portalOverview';
import {
  canDownloadPortalReceipt,
  formatPortalPaymentDate,
  formatPortalProviderReference,
  resolvePortalPaymentContext,
} from '@/modules/portal/utils/portalPaymentsHelpers';

export type PortalInvoiceRecord = {
  payment: PaymentDetail;
  invoiceReference: string | null;
  providerReference: string | null;
  documentDate: string;
  productName: string | null;
  planName: string | null;
  canDownloadReceipt: boolean;
};

const INVOICE_METADATA_KEYS = [
  'provider_invoice_id',
  'invoiceId',
  'InvoiceId',
  'invoice_id',
  'InvoiceReference',
] as const;

const MYFATOORAH_NESTED_KEYS = ['invoiceId', 'InvoiceId', 'invoice_id'] as const;

export function extractPortalInvoiceReference(metadata: unknown): string | null {
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) {
    return null;
  }

  const record = metadata as Record<string, unknown>;
  for (const key of INVOICE_METADATA_KEYS) {
    const value = record[key];
    if (typeof value === 'string' && value.trim().length > 0) return value.trim();
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  }

  const myfatoorah = record.myfatoorah;
  if (myfatoorah && typeof myfatoorah === 'object' && !Array.isArray(myfatoorah)) {
    const nested = myfatoorah as Record<string, unknown>;
    for (const key of MYFATOORAH_NESTED_KEYS) {
      const value = nested[key];
      if (typeof value === 'string' && value.trim().length > 0) return value.trim();
      if (typeof value === 'number' && Number.isFinite(value)) return String(value);
    }
  }

  return null;
}

/** Completed payments that can be shown as billing documents / receipts. */
export function portalInvoiceEligiblePayments(payments: PaymentDetail[]): PaymentDetail[] {
  return payments.filter((payment) => payment.status === 'succeeded');
}

export function mapPortalInvoiceRecords(
  payments: PaymentDetail[],
  overview: PortalOverview | undefined,
  locale: string,
): PortalInvoiceRecord[] {
  return portalInvoiceEligiblePayments(payments)
    .map((payment) => {
      const { productName, planName } = resolvePortalPaymentContext(payment, overview);
      return {
        payment,
        invoiceReference: extractPortalInvoiceReference(payment.metadata),
        providerReference: formatPortalProviderReference(payment),
        documentDate: formatPortalPaymentDate(payment, locale),
        productName,
        planName,
        canDownloadReceipt: canDownloadPortalReceipt(payment),
      };
    })
    .sort((a, b) => {
      const aIso = a.payment.processedAt ?? a.payment.createdAt;
      const bIso = b.payment.processedAt ?? b.payment.createdAt;
      return new Date(bIso).getTime() - new Date(aIso).getTime();
    });
}

export function portalInvoicesHasPaymentHistory(payments: PaymentDetail[]): boolean {
  return payments.length > 0;
}

export function portalInvoicesHasEligibleDocuments(payments: PaymentDetail[]): boolean {
  return portalInvoiceEligiblePayments(payments).length > 0;
}
